import { useCallback } from 'react';
import { useAPIContext } from '../context/api';
import type { EditorUploadAPI } from '../../../constructs/editor-upload/interface';
import type { UploadMediaFn } from '../../../../frontend-components/src/creativity-editor/shared/types/public';

/**
 * Centralised hook that produces the `uploadMedia` prop for CreativityEditor.
 *
 * Each host calls it with a service name (e.g. `'avatars'`).
 * The hook encapsulates:
 *   1. callEndpoint → backend generates a UUID key, returns { uploadUrl, publicUrl, uploadHeaders }
 *   2. fetch PUT → uploads the file directly to R2 via the presigned URL with metadata headers
 *
 * Returns an `UploadMediaFn` ready to be passed as a prop.
 */
export const useEditorUpload = (service: string): UploadMediaFn => {
  const { callEndpoint } = useAPIContext();

  return useCallback<UploadMediaFn>(
    async (file: File) => {
      const extension = file.name.includes('.') ? `.${file.name.split('.').pop()!}` : '';

      const { uploadUrl, publicUrl, uploadHeaders } = await callEndpoint<EditorUploadAPI, 'post'>(
        'editor-upload',
        '',
        'POST',
        {
          fileName: file.name,
          contentType: file.type,
          extension,
          service
        }
      );

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: uploadHeaders
      });

      if (!putResponse.ok) {
        const errorText = await putResponse.text();
        throw new Error(`Upload to R2 failed: ${putResponse.status} - ${errorText}`);
      }

      return { publicUrl };
    },
    [callEndpoint, service]
  );
};
